import type { Usuario } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EstadoCarregando,
  EstadoDeErro,
  EstadoVazio,
  Etiqueta,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabela,
  type ColunaDaTabela,
} from "@base/ui";
import { MoreHorizontal, UserPlus } from "lucide-react";
import { useState } from "react";
import {
  useAtualizarUsuario,
  useEnviarLinkDeSenha,
  useExcluirUsuario,
  useListarUsuarios,
  useSessao,
} from "../application/use-usuarios";
import { ROTULO_DO_PAPEL } from "../domain/papeis";
import { FILTRO_DA_CONTA_VAZIO, filtroDaContaAtivo, type FiltroDaConta } from "../../../shared/lista/filtro-da-conta";
import { BarraDeFiltrosDaLista } from "../../../shared/ui/barra-de-filtros-da-lista";
import { DialogoDoLinkDeSenha } from "../../../shared/ui/campo-de-link-copiavel";
import { FormularioDeNovoUsuario } from "./formulario-de-novo-usuario";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";

/** Gestao de usuarios pelo admin: listar, cadastrar, mudar papel e status. */
export function PaginaDeUsuarios() {
  const [cursores, setCursores] = useState<string[]>([]);
  const [filtro, setFiltro] = useState<FiltroDaConta>(FILTRO_DA_CONTA_VAZIO);
  const cursorAtual = cursores.at(-1);
  const consulta = {
    ...(filtro.busca.trim() ? { busca: filtro.busca.trim() } : {}),
    ...(filtro.papel ? { papel: filtro.papel as "admin" | "usuario" } : {}),
    ...(filtro.situacao ? { situacao: filtro.situacao as "ativo" | "convite" | "desativado" } : {}),
  };
  const lista = useListarUsuarios(cursorAtual, consulta);
  const [formularioAberto, setFormularioAberto] = useState(false);
  const [convite, setConvite] = useState<{ email: string; link: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<Usuario | null>(null);

  return (
    <div className="flex flex-col gap-secao">
      <CabecalhoDePagina
        rotulo="Gestão"
        titulo="Usuários"
        descricao="Todas as contas do painel. Aqui você cadastra a equipe Monte; as pessoas das clínicas recebem acesso em Acessos."
        acoes={
          <Button onClick={() => setFormularioAberto(true)}>
            <UserPlus aria-hidden="true" />
            Novo da equipe Monte
          </Button>
        }
      />

      <p role="status" aria-live="polite" className={aviso ? "rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground" : "sr-only"}>
        {aviso}
      </p>

      <BarraDeFiltrosDaLista
        valor={filtro}
        aoMudar={(proximo) => {
          setFiltro(proximo);
          setCursores([]);
        }}
        papeis={[
          { valor: "admin", rotulo: ROTULO_DO_PAPEL.admin },
          { valor: "usuario", rotulo: ROTULO_DO_PAPEL.usuario },
        ]}
      />

      {lista.isPending ? (
        <EstadoCarregando rotulo="Carregando usuários" />
      ) : lista.isError ? (
        <EstadoDeErro
          mensagem={mensagemDoErro(lista.error)}
          aoTentarNovamente={() => void lista.refetch()}
        />
      ) : lista.data.dados.length === 0 ? (
        <EstadoVazio
          titulo={filtroDaContaAtivo(filtro) ? "Nenhum resultado" : "Nenhum usuário cadastrado"}
          descricao={filtroDaContaAtivo(filtro) ? "Tente outro nome, e-mail ou filtro." : undefined}
        />
      ) : (
        <>
          <Tabela
            legenda="Usuários cadastrados"
            colunas={colunas(setAviso, setExcluindo)}
            itens={lista.data.dados}
            chaveDe={(usuario) => usuario.id}
          />
          <div className="flex justify-end gap-2">
            {cursores.length > 0 ? (
              <Button variant="outline" onClick={() => setCursores((atual) => atual.slice(0, -1))}>
                Página anterior
              </Button>
            ) : null}
            {lista.data.proximoCursor ? (
              <Button
                variant="outline"
                onClick={() => {
                  const proximo = lista.data.proximoCursor;
                  if (proximo) setCursores((atual) => [...atual, proximo]);
                }}
              >
                Próxima página
              </Button>
            ) : null}
          </div>
        </>
      )}

      <ConfirmacaoDeExclusao
        usuario={excluindo}
        aoFechar={() => setExcluindo(null)}
        aoExcluir={(usuario) => {
          setExcluindo(null);
          setAviso(`A conta de ${usuario.nome} foi excluída.`);
        }}
      />

      <Dialog open={formularioAberto} onOpenChange={setFormularioAberto}>
        <DialogContent tamanho="formulario">
          <DialogHeader rotulo="Equipe Monte">
            <DialogTitle>Novo da equipe Monte</DialogTitle>
            <DialogDescription>
              Quem é da equipe Monte acessa todos os clientes. Depois do cadastro você copia o
              link para a pessoa criar a senha; vale 72 horas.
            </DialogDescription>
          </DialogHeader>
          <FormularioDeNovoUsuario
            aoCriar={(criado) => {
              setFormularioAberto(false);
              setConvite({ email: criado.email, link: criado.linkDeSenha });
              setAviso(`Acesso criado para ${criado.email}. Copie o link e envie para a pessoa.`);
            }}
          />
        </DialogContent>
      </Dialog>

      <DialogoDoLinkDeSenha
        aberto={convite !== null}
        titulo="Link do convite"
        descricao={`Copie e envie para ${convite?.email}. Vale 72 horas.`}
        link={convite?.link ?? null}
        aoFechar={() => setConvite(null)}
      />
    </div>
  );
}

function colunas(
  avisar: (mensagem: string) => void,
  pedirExclusao: (usuario: Usuario) => void,
): readonly ColunaDaTabela<Usuario>[] {
  return [
    {
      chave: "nome",
      titulo: "Nome",
      celula: (usuario) => (
        <div className="flex flex-col">
          <span className="font-medium">{usuario.nome}</span>
          <span className="text-xs text-muted-foreground">{usuario.email}</span>
        </div>
      ),
    },
    {
      chave: "papel",
      titulo: "Papel",
      celula: (usuario) => (
        <Etiqueta tom={usuario.papel === "admin" ? "secundario" : "contorno"}>
          {ROTULO_DO_PAPEL[usuario.papel]}
        </Etiqueta>
      ),
    },
    {
      chave: "situacao",
      titulo: "Situação",
      ocultarNoCelular: true,
      celula: (usuario) =>
        !usuario.ativo ? (
          <Etiqueta tom="neutro">Desativado</Etiqueta>
        ) : usuario.senhaDefinida ? (
          <Etiqueta tom="sucesso">Ativo</Etiqueta>
        ) : (
          <Etiqueta tom="atencao">Convite pendente</Etiqueta>
        ),
    },
    {
      chave: "acoes",
      titulo: "Ações",
      celula: (usuario) => (
        <AcoesDoUsuario usuario={usuario} avisar={avisar} pedirExclusao={pedirExclusao} />
      ),
    },
  ];
}

function AcoesDoUsuario({
  usuario,
  avisar,
  pedirExclusao,
}: {
  readonly usuario: Usuario;
  readonly avisar: (mensagem: string) => void;
  readonly pedirExclusao: (usuario: Usuario) => void;
}) {
  const sessao = useSessao();
  const atualizar = useAtualizarUsuario();
  const enviarLink = useEnviarLinkDeSenha();
  const [link, setLink] = useState<string | null>(null);
  const ehVoceMesmo = sessao.data?.usuario.id === usuario.id;
  const ocupado = atualizar.isPending || enviarLink.isPending;

  function alterar(dados: { papel?: "admin" | "usuario"; ativo?: boolean }, sucesso: string) {
    atualizar.mutate(
      { id: usuario.id, dados },
      { onSuccess: () => avisar(sucesso), onError: (erro) => avisar(mensagemDoErro(erro)) },
    );
  }

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" disabled={ocupado} aria-label={`Ações para ${usuario.nome}`}>
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {usuario.ativo ? (
          <DropdownMenuItem
            onSelect={() =>
              enviarLink.mutate(usuario.id, {
                onSuccess: (resposta) => {
                  setLink(resposta.linkDeSenha);
                  avisar(`Link gerado. Copie e envie para ${usuario.email}.`);
                },
                onError: (erro) => avisar(mensagemDoErro(erro)),
              })
            }
          >
            {usuario.senhaDefinida ? "Redefinir senha" : "Gerar link do convite"}
          </DropdownMenuItem>
        ) : null}
        {!ehVoceMesmo ? (
          <>
            <DropdownMenuItem
              onSelect={() =>
                alterar(
                  { papel: usuario.papel === "admin" ? "usuario" : "admin" },
                  usuario.papel === "admin"
                    ? `${usuario.nome} saiu da equipe Monte.`
                    : `${usuario.nome} agora é da equipe Monte e acessa todos os clientes.`,
                )
              }
            >
              {usuario.papel === "admin" ? "Remover da equipe Monte" : "Adicionar à equipe Monte"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant={usuario.ativo ? "destructive" : "default"}
              onSelect={() =>
                alterar(
                  { ativo: !usuario.ativo },
                  usuario.ativo
                    ? `${usuario.nome} foi desativado e saiu de todas as sessões.`
                    : `${usuario.nome} foi reativado.`,
                )
              }
            >
              {usuario.ativo ? "Desativar" : "Reativar"}
            </DropdownMenuItem>
          </>
        ) : null}
        {usuario.papel !== "admin" ? (
          <DropdownMenuItem variant="destructive" onSelect={() => pedirExclusao(usuario)}>
            Excluir conta
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
      <DialogoDoLinkDeSenha
        aberto={link !== null}
        titulo={usuario.senhaDefinida ? "Link de redefinição" : "Link do convite"}
        descricao={`Copie e envie para ${usuario.email}. Vale 72 horas.`}
        link={link}
        aoFechar={() => setLink(null)}
      />
    </>
  );
}

/**
 * Confirmacao antes de excluir: a acao nao tem "desfazer" na tela. Diz o que
 * acontece em vez de perguntar "tem certeza?".
 */
function ConfirmacaoDeExclusao({
  usuario,
  aoFechar,
  aoExcluir,
}: {
  readonly usuario: Usuario | null;
  readonly aoFechar: () => void;
  readonly aoExcluir: (usuario: Usuario) => void;
}) {
  const excluir = useExcluirUsuario();

  return (
    <Dialog
      open={usuario !== null}
      onOpenChange={(aberto) => {
        if (!aberto) {
          excluir.reset();
          aoFechar();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir a conta de {usuario?.nome}?</DialogTitle>
          <DialogDescription>
            {usuario?.email} perde o acesso a todos os clientes na hora e não consegue mais entrar.
            Convites e links de senha pendentes deixam de valer. O histórico de auditoria é mantido.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {excluir.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {mensagemDoErro(excluir.error)}
            </p>
          ) : null}
          <Button
            variant="destructive"
            disabled={excluir.isPending || usuario === null}
            onClick={() => {
              if (usuario) excluir.mutate(usuario.id, { onSuccess: () => aoExcluir(usuario) });
            }}
          >
            {excluir.isPending ? "Excluindo…" : "Excluir conta"}
          </Button>
          <Button variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
